// 🌿 Herbicide Timing Calculator Logic
// Calculates optimal timing for pre-emergent and post-emergent herbicide applications

import { GRASS_CHARACTERISTICS } from "@/data/grass-characteristics"
import { WEED_CHARACTERISTICS, getWeedsByCategory } from "@/data/weed-types"
import { HERBICIDE_PRODUCTS, getProductsForWeeds, getProductsForGrassType } from "@/data/herbicide-products"
import { getRegionFromZipCode, REGIONAL_CLIMATE_DATA } from "@/data/regional-climate"
import { getCurrentSeason } from "./date-utils"
import {
  attachToolEngineMetadata,
  buildCanonicalToolContext,
  type DeterministicToolResultMetadata,
} from "@/lib/lawn-plan-engine"

export interface HerbicideTimingResult extends DeterministicToolResultMetadata {
  zipCode: string
  region: string
  grassType: string
  targetWeeds: string[]
  applicationType: 'pre-emergent' | 'post-emergent' | 'both'
  
  // Timing recommendations
  preEmergentTiming?: {
    optimalWindow: string
    criticalDate: string
    soilTempRequirement: string
    urgencyLevel: 'critical' | 'optimal' | 'late' | 'too-late'
    daysRemaining: number
    status: string
    /** When the next window opens, set when this season's window is over. */
    nextWindow?: string
    /**
     * Set in fall when every targeted weed is a summer annual (crabgrass,
     * foxtail, goosegrass, spurge): the plants die with the first hard frost,
     * so there is nothing to spray and the answer is next spring's window.
     */
    seasonOver?: { weeds: string[] }
    /**
     * Set when today's soil temperature decides the answer (fall winter-annual
     * weeds), so the window follows soil the way the plan, Home and the soil
     * tool do instead of a calendar range.
     */
    soilBasis?: {
      soilTempF: number
      stage: 'waiting' | 'open' | 'passed'
      opensBelowF: number
      closesBelowF: number
      weeds: string[]
    }
  }
  
  postEmergentTiming?: {
    optimalWindow: string
    currentConditions: string
    growthStage: string
    weatherRequirements: string[]
    status: string
    /** Set when today's weather says to hold the spray even though the season fits. */
    hold?: { reason: 'heat' | 'rain' | 'cold'; text: string }
  }
  
  // Product recommendations
  recommendedProducts: {
    preEmergent: any[]
    postEmergent: any[]
    combined: any[]
  }
  
  // Safety and application info
  applicationTips: string[]
  safetyPrecautions: string[]
  safetyLimitations: string[]
  followUpSchedule: string[]
  costEstimate: {
    preEmergent: number
    postEmergent: number
    total: number
  }
  
  // Regional considerations
  regionalNotes: string[]
  weatherConsiderations: string[]
  
  // Current status
  currentStatus: string
  nextAction: string

  // Which approach (pre/post) the UI should foreground for this user. Driven
  // by the user's applicationType plus, when they picked 'both', a
  // time-relevance check against the pre-emergent window. 'none' is
  // defensive — at least one weed is required so it should never appear.
  recommendedApproach: 'pre-emergent' | 'post-emergent' | 'none'
}

/**
 * Soil temperature (6 in) where a fall pre-emergent opens as soil cools, and
 * where it has passed. Mirrors the plan engine's fall_pre_emergent rule and
 * the soil trigger table in mobile/lib/plan-soil-triggers.ts (70°F), and the
 * map's passed threshold in mobile/lib/map-implications.ts (55°F).
 */
export const FALL_PRE_EMERGENT_OPENS_BELOW_F = 70
export const FALL_PRE_EMERGENT_PASSED_BELOW_F = 55

/** Forecast highs where post-emergent labels (2,4-D, dicamba, triclopyr) say stop or spray only in the cool of the day. */
export const POST_EMERGENT_HOLD_HIGH_F = 90
export const POST_EMERGENT_CAUTION_HIGH_F = 85
export const POST_EMERGENT_COLD_HIGH_F = 50

/** What is happening right now at the lawn, from the same soil and weather reads Home uses. */
export interface HerbicideConditions {
  /** Soil temperature at the lawn, °F. */
  soilTempF?: number | null
  /** Today's forecast high (or the current air temperature), °F. */
  airHighF?: number | null
  /** Rain is expected within the next few days. */
  rainSoon?: boolean | null
  rainSoonInches?: number | null
}

export function calculateHerbicideTiming(
  zipCode: string,
  grassType: string,
  targetWeeds: string[],
  applicationType: 'pre-emergent' | 'post-emergent' | 'both',
  options?: {
    lawnSize?: string | number | null
    sunlight?: string | null
    conditions?: HerbicideConditions | null
  },
): HerbicideTimingResult {
  const context = buildCanonicalToolContext({
    toolId: 'herbicide',
    grassType,
    lawnSize: options?.lawnSize,
    sunlight: options?.sunlight,
  })
  // Validate inputs
  const grass = GRASS_CHARACTERISTICS[context.grassType]
  if (!grass) {
    throw new Error('Herbicide timing data is not available for this grass type.')
  }

  const region = getRegionFromZipCode(zipCode)
  const climateData = REGIONAL_CLIMATE_DATA[region]
  if (!climateData) {
    throw new Error('We could not determine your region from this ZIP code. Try a nearby ZIP.')
  }

  // Get current season and date info
  const currentSeason = getCurrentSeason()
  const currentDate = new Date()
  const currentMonth = currentDate.getMonth() + 1 // 1-12
  const conditions = options?.conditions ?? undefined

  // Get weed characteristics for target weeds
  const weedData = []
  for (const weedName of targetWeeds) {
    const weed = WEED_CHARACTERISTICS[weedName]
    if (!weed) {
      throw new Error(`Weed type not recognized: ${weedName}. Please select from the available options.`)
    }
    weedData.push(weed)
  }

  // Calculate pre-emergent timing if needed.
  // For 'both', filter out weeds where pre-emergent is agronomically marginal
  // (perennial broadleafs in the post-emergent category). When the user
  // explicitly chose 'pre-emergent', honour that — the form already prevents
  // picking post-emergent-only weeds in that mode.
  let preEmergentTiming: HerbicideTimingResult['preEmergentTiming'] | undefined
  if (applicationType === 'pre-emergent' || applicationType === 'both') {
    const candidates =
      applicationType === 'both'
        ? weedData.filter((w) => w.category !== 'post-emergent')
        : weedData
    if (candidates.length > 0) {
      preEmergentTiming = calculatePreEmergentTiming(candidates, climateData, currentMonth, region, conditions, weedData)
    }
  }

  // Summer annuals in fall: nothing to spray, the answer is next spring's
  // pre-emergent window, so no post-emergent plan is offered beside it.
  const seasonOver = Boolean(preEmergentTiming?.seasonOver)

  // Calculate post-emergent timing if needed
  let postEmergentTiming: HerbicideTimingResult['postEmergentTiming'] | undefined
  if ((applicationType === 'post-emergent' || applicationType === 'both') && !seasonOver) {
    postEmergentTiming = calculatePostEmergentTiming(weedData, climateData, currentSeason, region, conditions)
  }

  // Fallback compute: when the user's chosen approach can't help this
  // season, also compute the alternative so the UI can show "switch to X"
  // with X's data actually rendered. We do NOT override recommendedApproach
  // here — the EdgeCard/hero copy still leads with "you missed pre" so the
  // educational moment is preserved; this just lets the post ApproachCard
  // render alongside it.
  if (
    applicationType === 'pre-emergent' &&
    preEmergentTiming?.urgencyLevel === 'too-late' &&
    !seasonOver &&
    !postEmergentTiming
  ) {
    postEmergentTiming = calculatePostEmergentTiming(weedData, climateData, currentSeason, region, conditions)
  }
  if (applicationType === 'post-emergent' && postEmergentTiming && !preEmergentTiming) {
    const c = postEmergentTiming.currentConditions.toLowerCase()
    const postDormant = c.includes('dormant') || c.includes('poor')
    if (postDormant) {
      const candidates = weedData.filter((w) => w.applicationTiming.preEmergent.urgencyDays > 0)
      if (candidates.length > 0) {
        preEmergentTiming = calculatePreEmergentTiming(candidates, climateData, currentMonth, region, conditions)
      }
    }
  }

  // Get product recommendations
  const recommendedProducts = getProductRecommendations(targetWeeds, context.grassType, applicationType)

  // Calculate cost estimates
  const costEstimate = calculateCostEstimates(recommendedProducts, targetWeeds)

  // Generate application tips and safety info
  const applicationTips = generateApplicationTips(context.grassType, targetWeeds, applicationType)
  if (preEmergentTiming?.soilBasis && grass.season === 'Cool') {
    applicationTips.unshift('Skip pre-emergent if you plan to overseed this fall. It blocks new seed from sprouting.')
  }
  const safetyPrecautions = generateSafetyPrecautions(context.grassType, targetWeeds)
  const safetyLimitations = generateSafetyLimitations(recommendedProducts, targetWeeds, context.grassType, applicationType)
  const followUpSchedule = generateFollowUpSchedule(applicationType, currentSeason)

  // Regional and weather considerations
  const regionalNotes = generateRegionalNotes(region, climateData, targetWeeds)
  const weatherConsiderations = generateWeatherConsiderations(currentSeason, region)

  // Decide which approach should foreground the UI before we compute
  // status/nextAction so those messages reflect the leading approach.
  const recommendedApproach = determineRecommendedApproach(
    applicationType,
    preEmergentTiming,
    postEmergentTiming
  )

  // Determine current status and next action
  const currentStatus = determineCurrentStatus(preEmergentTiming, postEmergentTiming, recommendedApproach)
  const nextAction = determineNextAction(preEmergentTiming, postEmergentTiming, recommendedApproach)

  return attachToolEngineMetadata({
    zipCode,
    region,
    grassType: context.grassType,
    targetWeeds,
    applicationType,
    preEmergentTiming,
    postEmergentTiming,
    recommendedProducts,
    applicationTips,
    safetyPrecautions,
    safetyLimitations,
    followUpSchedule,
    costEstimate,
    regionalNotes,
    weatherConsiderations,
    currentStatus,
    nextAction,
    recommendedApproach
  }, context)
}

/**
 * Decide which approach (pre/post) the UI should lead with.
 *
 * - When the user explicitly picked pre- or post-emergent, honour that.
 * - For 'both', pre-emergent leads only when its window is now or imminent
 *   (-14 to +60 days). Outside that range, post-emergent leads — either
 *   because there's a visible-weed problem to solve right now, or because
 *   the pre-emergent window is months away and shouldn't dominate the hero.
 * - Falls back to whichever timing exists when one is undefined (e.g.,
 *   user picked 'both' with all post-emergent-only weeds, so the engine
 *   produced no preEmergentTiming).
 */
function determineRecommendedApproach(
  applicationType: 'pre-emergent' | 'post-emergent' | 'both',
  preEmergentTiming: HerbicideTimingResult['preEmergentTiming'] | undefined,
  postEmergentTiming: HerbicideTimingResult['postEmergentTiming'] | undefined
): HerbicideTimingResult['recommendedApproach'] {
  if (applicationType !== 'both') return applicationType
  if (!preEmergentTiming && !postEmergentTiming) return 'none'
  if (!preEmergentTiming) return 'post-emergent'
  if (!postEmergentTiming) return 'pre-emergent'

  // Pre-emergent leads when its window is now or imminent. Use urgencyLevel
  // semantics directly (not raw days) so the boundary at days = -14 lines up
  // with the calculator's own 'too-late' threshold.
  const soil = preEmergentTiming.soilBasis
  if (soil?.stage === 'open') return 'pre-emergent'
  if (soil?.stage === 'waiting') {
    return postIsDormant(postEmergentTiming) ? 'pre-emergent' : 'post-emergent'
  }

  const u = preEmergentTiming.urgencyLevel
  const preNearTerm =
    u === 'critical' ||
    u === 'late' ||
    (u === 'optimal' && preEmergentTiming.daysRemaining <= 60)
  if (preNearTerm) return 'pre-emergent'

  // Pre is months out OR too-late.
  // If pre is too-late this season, post is the only path (even if dormant —
  // the user sees post's dormant message which is honest).
  if (u === 'too-late') return 'post-emergent'

  // Pre is months out (window will return). Post leads when actionable.
  // When post is dormant (winter), fall back to pre's calendar/planning view —
  // it gives the user a future date to set a reminder, which beats a dormant
  // "wait until spring" with no concrete next step.
  if (postIsDormant(postEmergentTiming)) return 'pre-emergent'
  return 'post-emergent'
}

function postIsDormant(
  postEmergentTiming: NonNullable<HerbicideTimingResult['postEmergentTiming']>
): boolean {
  const c = postEmergentTiming.currentConditions.toLowerCase()
  return c.includes('dormant') || c.includes('poor')
}

function isFallWeed(weed: any): boolean {
  const weedWindow = weed.applicationTiming.preEmergent.timingWindow.toLowerCase()
  return /fall|summer|august|september|october/i.test(weedWindow)
}

/**
 * A summer annual grass or broadleaf (crabgrass, foxtail, goosegrass, spotted
 * spurge): pre-emergent works on it, it sprouts in spring, and its post-emergent
 * timing has no fall window. The plants die with the first hard frost.
 */
function isSummerAnnual(weed: any): boolean {
  if (weed.applicationTiming.preEmergent.urgencyDays <= 0) return false
  if (isFallWeed(weed)) return false
  const post: string[] = weed.applicationTiming.postEmergent?.seasonalTiming ?? []
  return !post.some((timing) => /fall|autumn|september|october|november/i.test(timing))
}

/** September through November: summer annuals are finished for the year. */
function summerAnnualsDone(weeds: any[], currentMonth: number): boolean {
  return currentMonth >= 9 && currentMonth <= 11 && weeds.length > 0 && weeds.every(isSummerAnnual)
}

function formatList(items: string[]): string {
  if (items.length <= 1) return items[0] ?? ''
  if (items.length === 2) return `${items[0]} and ${items[1]}`
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`
}

/**
 * The fall pre-emergent call for winter annuals (Poa annua, henbit, chickweed).
 * Soil temperature decides it, the same rule the plan, Home, the map and the
 * soil tool use: it opens as soil cools below 70°F and has passed below 55°F,
 * when the winter flush is already up. Returns undefined outside late summer
 * through fall, when no soil reading is available, or when no fall weed is
 * targeted, so the calendar answer still runs.
 */
function calculateFallSoilPreEmergent(
  weedData: any[],
  currentMonth: number,
  conditions: HerbicideConditions | undefined,
): HerbicideTimingResult['preEmergentTiming'] | undefined {
  const soilF = conditions?.soilTempF
  if (typeof soilF !== 'number' || !Number.isFinite(soilF)) return undefined
  if (currentMonth < 7 || currentMonth > 12) return undefined
  const fallWeeds = weedData.filter(
    (w) => w.applicationTiming.preEmergent.urgencyDays > 0 && isFallWeed(w),
  )
  if (fallWeeds.length === 0) return undefined

  const stage: 'waiting' | 'open' | 'passed' =
    soilF > FALL_PRE_EMERGENT_OPENS_BELOW_F
      ? 'waiting'
      : soilF >= FALL_PRE_EMERGENT_PASSED_BELOW_F
        ? 'open'
        : 'passed'
  if (stage === 'passed') return undefined

  const names = fallWeeds.map((w) => w.name as string)
  const reading = Math.round(soilF)
  const soilBasis = {
    soilTempF: reading,
    stage,
    opensBelowF: FALL_PRE_EMERGENT_OPENS_BELOW_F,
    closesBelowF: FALL_PRE_EMERGENT_PASSED_BELOW_F,
    weeds: names,
  }
  const criticalDate = fallWeeds[0].applicationTiming.preEmergent.criticalDates

  if (stage === 'open') {
    return {
      optimalWindow: `Soil ${FALL_PRE_EMERGENT_PASSED_BELOW_F}-${FALL_PRE_EMERGENT_OPENS_BELOW_F}°F`,
      criticalDate,
      soilTempRequirement: `${FALL_PRE_EMERGENT_PASSED_BELOW_F}-${FALL_PRE_EMERGENT_OPENS_BELOW_F}°F`,
      urgencyLevel: soilF <= FALL_PRE_EMERGENT_PASSED_BELOW_F + 5 ? 'critical' : 'optimal',
      daysRemaining: 0,
      status: `Soil is ${reading}°F, so the fall window for ${formatList(names)} is open. Apply before soil drops below ${FALL_PRE_EMERGENT_PASSED_BELOW_F}°F.`,
      soilBasis,
    }
  }
  return {
    optimalWindow: `Soil below ${FALL_PRE_EMERGENT_OPENS_BELOW_F}°F`,
    criticalDate,
    soilTempRequirement: `Below ${FALL_PRE_EMERGENT_OPENS_BELOW_F}°F`,
    urgencyLevel: 'optimal',
    daysRemaining: 30,
    status: `Soil is ${reading}°F. The fall window for ${formatList(names)} opens once soil cools below ${FALL_PRE_EMERGENT_OPENS_BELOW_F}°F.`,
    soilBasis,
  }
}

function calculatePreEmergentTiming(
  weedData: any[],
  climateData: any,
  currentMonth: number,
  region: string,
  conditions?: HerbicideConditions,
  /** Every weed the user targeted; the summer-annual verdict needs all of them. */
  allTargets: any[] = weedData,
): HerbicideTimingResult['preEmergentTiming'] | undefined {
  return (
    calculateFallSoilPreEmergent(weedData, currentMonth, conditions) ??
    calculateCalendarPreEmergentTiming(weedData, climateData, currentMonth, region, allTargets)
  )
}

function calculateCalendarPreEmergentTiming(
  weedData: any[],
  climateData: any,
  currentMonth: number,
  region: string,
  allTargets: any[] = weedData,
): HerbicideTimingResult['preEmergentTiming'] | undefined {
  // For each weed, determine its window and how urgent it is.
  // Use the regional pre-emergent window for spring weeds (soil temp <= 65°F),
  // and the weed's own timing for fall weeds (like Poa annua).
  const now = new Date()

  // Collect all valid windows with their days remaining
  const candidates: { weed: any; window: string; days: number }[] = []

  for (const weed of weedData) {
    const soilTemp = weed.applicationTiming.preEmergent.soilTempTarget
    // Skip weeds where pre-emergent is ineffective (nutsedge, creeping charlie, wild violet)
    if (weed.applicationTiming.preEmergent.urgencyDays === 0) continue

    // Fall-germinating weeds (Poa annua, Henbit, Chickweed) keep their own timing window.
    // Spring-germinating weeds use the regional pre-emergent window when available.
    const weedWindow = weed.applicationTiming.preEmergent.timingWindow.toLowerCase()
    const isFallWeed = /fall|summer|august|september|october/i.test(weedWindow)
    let window = weed.applicationTiming.preEmergent.timingWindow
    if (!isFallWeed && climateData.criticalTiming) {
      window = climateData.criticalTiming.preEmergentWindow
    }

    const parsedWindow = parseTimingWindow(window)
    if (parsedWindow) {
      const days = Math.round((parsedWindow.end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
      candidates.push({ weed, window, days })
    }
  }

  if (candidates.length === 0) {
    return undefined
  }

  // Tier 1: a window currently open or closing within ~60 days. Pick the soonest-closing.
  // Tier 2: otherwise the candidate closest to today by absolute distance — tells the user
  // accurately whether they just missed a spring window or the next opening is months out.
  const open = candidates.filter((c) => c.days >= 0 && c.days <= 60)
  const best =
    open.length > 0
      ? open.reduce((a, b) => (a.days < b.days ? a : b))
      : candidates.reduce((a, b) => (Math.abs(a.days) < Math.abs(b.days) ? a : b))

  const bestWeed = best.weed
  let bestWindow = best.window
  const chosenEnd = parseWindowEndDate(bestWindow)
  if (chosenEnd) {
    bestWindow = reduceWindowToChosenHalf(bestWindow, chosenEnd)
  }

  const soilTempTarget = bestWeed.applicationTiming.preEmergent.soilTempTarget
  const urgencyDays = bestWeed.applicationTiming.preEmergent.urgencyDays
  const optimalWindow = bestWindow
  const criticalDate = bestWeed.applicationTiming.preEmergent.criticalDates

  // Calculate urgency based on actual window end date
  let urgencyLevel: 'critical' | 'optimal' | 'late' | 'too-late'
  let daysRemaining = 0
  let status = ''
  let nextWindow: string | undefined
  let seasonOver: { weeds: string[] } | undefined
  let requirementF = soilTempTarget

  const windowEndDate = parseWindowEndDate(optimalWindow)
  if (windowEndDate) {
    const diffMs = windowEndDate.getTime() - now.getTime()
    daysRemaining = Math.round(diffMs / (1000 * 60 * 60 * 24))

    if (daysRemaining > urgencyDays) {
      urgencyLevel = 'optimal'
      status = `You have ${daysRemaining} days until the application window closes.`
    } else if (daysRemaining > 0) {
      urgencyLevel = 'critical'
      status = `Only ${daysRemaining} days left in the application window. Apply soon.`
    } else if (daysRemaining > -14) {
      urgencyLevel = 'late'
      status = 'The optimal window has passed. Some effectiveness may be reduced.'
    } else {
      urgencyLevel = 'too-late'
      status = 'Too late for pre-emergent this season. Focus on post-emergent control.'
      nextWindow = isFallWeed(bestWeed)
        ? `fall, once soil cools below ${FALL_PRE_EMERGENT_OPENS_BELOW_F}°F`
        : `${(climateData.criticalTiming?.preEmergentWindow as string | undefined) ?? 'early spring'}, when soil warms toward ${soilTempTarget}°F`
      if (summerAnnualsDone(allTargets, currentMonth)) {
        const names = weedData.map((w) => w.name as string)
        // The first of them to sprout sets the date: crabgrass at 55°F comes
        // before spurge at 60°F.
        const earliestF = Math.min(...weedData.map((w) => w.applicationTiming.preEmergent.soilTempTarget as number))
        seasonOver = { weeds: names }
        requirementF = earliestF
        status = `${formatList(names)} ${names.length > 1 ? 'die' : 'dies'} with the first hard frost, so there is nothing to spray this fall. Plan a pre-emergent next spring before soil reaches ${earliestF}°F.`
        nextWindow = `${(climateData.criticalTiming?.preEmergentWindow as string | undefined) ?? 'early spring'}, when soil warms toward ${earliestF}°F`
      }
    }
  } else {
    urgencyLevel = 'optimal'
    daysRemaining = 0
    status = bestWeed.applicationTiming.preEmergent.timingWindow
  }

  return {
    optimalWindow,
    criticalDate,
    soilTempRequirement: `${requirementF}°F`,
    urgencyLevel,
    daysRemaining,
    status,
    ...(nextWindow ? { nextWindow } : {}),
    ...(seasonOver ? { seasonOver } : {}),
  }
}

function calculatePostEmergentTiming(
  weedData: any[],
  climateData: any,
  currentSeason: string,
  region: string,
  conditions?: HerbicideConditions,
): HerbicideTimingResult['postEmergentTiming'] {
  // Get the most common timing from all target weeds
  const allTimings = weedData.flatMap(weed => weed.applicationTiming.postEmergent.seasonalTiming)
  const mostCommonTiming = allTimings[0] || 'Spring or fall when actively growing'

  let optimalWindow = mostCommonTiming
  let currentConditions = ''
  let growthStage = 'Actively growing'
  let weatherRequirements = ['Apply when weeds are dry', 'No rain for 24 hours', 'Temperatures 60-80°F']
  let status = ''

  // Adjust for current season
  switch (currentSeason) {
    case 'spring':
      currentConditions = 'Good conditions for post-emergent application'
      status = 'Optimal time for post-emergent herbicide application.'
      break
    case 'summer':
      currentConditions = 'Hot weather - avoid application during peak heat'
      weatherRequirements = ['Apply early morning or evening', 'Avoid temperatures above 85°F', 'Ensure adequate soil moisture']
      status = 'Use caution with post-emergent herbicide in summer heat. Apply early morning or evening.'
      break
    case 'fall':
      currentConditions = 'Excellent conditions for post-emergent application'
      status = 'Perfect time for post-emergent herbicide application.'
      break
    case 'winter':
      currentConditions = 'Poor conditions - weeds dormant'
      status = 'Weeds are dormant. Wait for active growth in spring.'
      break
  }

  // Today's weather can hold a spray the season would allow. Only when there is
  // a reading, and never in dormant winter, which already says wait.
  let hold: NonNullable<HerbicideTimingResult['postEmergentTiming']>['hold'] | undefined
  if (currentSeason !== 'winter' && conditions) {
    const high = conditions.airHighF
    const hasHigh = typeof high === 'number' && Number.isFinite(high)
    if (hasHigh && (high as number) >= POST_EMERGENT_HOLD_HIGH_F) {
      const text = `Highs near ${Math.round(high as number)}°F are too hot to spray. Labels limit post-emergent use above ${POST_EMERGENT_CAUTION_HIGH_F}-${POST_EMERGENT_HOLD_HIGH_F}°F, so wait for cooler days.`
      hold = { reason: 'heat', text }
      currentConditions = 'Too hot for post-emergent today'
      weatherRequirements = [
        `Forecast high ${Math.round(high as number)}°F: wait for highs under ${POST_EMERGENT_CAUTION_HIGH_F}°F`,
        'Spraying in heat can burn turf and drift onto nearby plants',
        'Then spray on a calm, dry morning',
      ]
    } else if (conditions.rainSoon) {
      const inches = conditions.rainSoonInches
      const amount = typeof inches === 'number' && inches > 0 ? ` (about ${inches.toFixed(1)} in)` : ''
      const text = `Rain is forecast in the next few days${amount}. Most labels need 6 to 24 dry hours after spraying, so wait for a dry stretch.`
      hold = { reason: 'rain', text }
      currentConditions = 'Rain in the forecast'
      weatherRequirements = [
        'Rain is forecast in the next few days',
        'Labels need 6 to 24 dry hours after spraying',
        'Spray once the rain passes and leaves are dry',
      ]
    } else if (hasHigh && (high as number) < POST_EMERGENT_COLD_HIGH_F) {
      const text = `Highs near ${Math.round(high as number)}°F are too cool for weeds to take up a spray. Wait for highs above ${POST_EMERGENT_COLD_HIGH_F}°F.`
      hold = { reason: 'cold', text }
      currentConditions = 'Too cool for post-emergent today'
      weatherRequirements = [
        `Forecast high ${Math.round(high as number)}°F: wait for highs above ${POST_EMERGENT_COLD_HIGH_F}°F`,
        'Weeds must be actively growing to take up the spray',
        'Then spray on a dry day',
      ]
    } else if (hasHigh && (high as number) >= POST_EMERGENT_CAUTION_HIGH_F) {
      weatherRequirements = [
        `Forecast high ${Math.round(high as number)}°F: spray early morning only`,
        'Avoid spraying above 85°F',
        'No rain for 24 hours',
      ]
    }
    if (hold) status = hold.text
  }

  return {
    optimalWindow,
    currentConditions,
    growthStage,
    weatherRequirements,
    status,
    ...(hold ? { hold } : {}),
  }
}

function getProductRecommendations(
  targetWeeds: string[],
  grassType: string,
  applicationType: string
): HerbicideTimingResult['recommendedProducts'] {
  // Get products suitable for target weeds and grass type
  const suitableProducts = getProductsForWeeds(targetWeeds).filter(product => 
    product.safeGrassTypes.includes(grassType)
  )

  const preEmergent = suitableProducts.filter(p => p.type === 'pre-emergent')
  const postEmergent = suitableProducts.filter(p => p.type === 'post-emergent' || p.type === 'selective')
  const combined = suitableProducts.filter(p => p.type === 'selective')

  return {
    preEmergent: preEmergent.slice(0, 3), // Top 3 recommendations
    postEmergent: postEmergent.slice(0, 3),
    combined: combined.slice(0, 2)
  }
}

function calculateCostEstimates(
  recommendedProducts: HerbicideTimingResult['recommendedProducts'],
  targetWeeds: string[]
): HerbicideTimingResult['costEstimate'] {
  // Calculate average cost per 1000 sq ft for each category (user typically buys ONE product)
  const preEmergentAvgCost = recommendedProducts.preEmergent.length > 0 
    ? recommendedProducts.preEmergent.reduce((sum, product) => 
        sum + (product.costEstimate?.per1000SqFt || 0), 0) / recommendedProducts.preEmergent.length
    : 0
  
  const postEmergentAvgCost = recommendedProducts.postEmergent.length > 0 
    ? recommendedProducts.postEmergent.reduce((sum, product) => 
        sum + (product.costEstimate?.per1000SqFt || 0), 0) / recommendedProducts.postEmergent.length
    : 0

  // Estimate for 5000 sq ft lawn (average size)
  const lawnSize = 5000
  const multiplier = lawnSize / 1000

  return {
    preEmergent: parseFloat((preEmergentAvgCost * multiplier).toFixed(2)),
    postEmergent: parseFloat((postEmergentAvgCost * multiplier).toFixed(2)),
    total: parseFloat(((preEmergentAvgCost + postEmergentAvgCost) * multiplier).toFixed(2))
  }
}

function generateApplicationTips(
  grassType: string,
  targetWeeds: string[],
  applicationType: string
): string[] {
  const tips = [
    'Read and follow all label instructions carefully',
    'Apply evenly to avoid streaks and missed areas',
    'Water in granular products immediately after application',
    'Avoid mowing for 2-3 days before and after application'
  ]

  if (grassType.includes('Bermuda') || grassType.includes('Zoysia')) {
    tips.push('Warm-season grasses may require different application timing')
  }

  if (targetWeeds.includes('Crabgrass')) {
    tips.push('Crabgrass control is most effective as pre-emergent')
  }

  if (applicationType === 'pre-emergent') {
    tips.push('Apply before soil reaches target temperature')
    tips.push('Ensure good soil moisture for activation')
  }

  return tips
}

function generateSafetyPrecautions(
  grassType: string,
  targetWeeds: string[]
): string[] {
  return [
    'Verify the product label lists your grass type, target weed, application site, rate, timing, and watering instructions before applying',
    'Do not exceed the label maximum per application or annual maximum rate',
    'Respect label reseeding, sodding, and sprigging intervals before planting new grass',
    'Wear protective clothing, gloves, and eyewear',
    'Keep children and pets off treated areas until dry',
    'Store products in original containers away from children',
    'Do not apply near water bodies to prevent runoff',
    'Avoid spraying during high wind, drought stress, saturated soil, or temperature extremes',
    'Check state and local restrictions before buying or applying restricted-use products',
    'Wash hands thoroughly after application',
    'Apply only to areas with existing weeds or weed pressure'
  ]
}

function generateSafetyLimitations(
  recommendedProducts: HerbicideTimingResult['recommendedProducts'],
  targetWeeds: string[],
  grassType: string,
  applicationType: string,
): string[] {
  const products = [
    ...recommendedProducts.preEmergent,
    ...recommendedProducts.postEmergent,
    ...recommendedProducts.combined,
  ]
  const uniqueActiveIngredients = Array.from(new Set(products.flatMap((p) => p.activeIngredients ?? [])))
  const limitations = [
    'This calculator screens broad timing, weed fit, and grass compatibility only. The product label is the legal source for rate, site, PPE, irrigation, and re-entry directions.',
    'Rates shown here are planning estimates, not a spray mix calculator. Measure your treated area and use the rate table on the exact label you buy.',
    'We do not yet model state registrations, restricted-use status, max annual rate, reseeding interval, adjuvant rules, or buffer zones product by product.',
  ]

  if (targetWeeds.length === 0) {
    limitations.unshift('No target weed was selected, so do not apply from this result until you identify the weed and confirm it appears on the label.')
  }

  if (uniqueActiveIngredients.length > 0) {
    limitations.push(
      `Active ingredients surfaced for this result include ${uniqueActiveIngredients.slice(0, 4).join(', ')}. Check each active ingredient against your ${grassType} lawn and local rules.`,
    )
  }

  if (applicationType === 'both') {
    limitations.push('If using both pre-emergent and post-emergent products, check label restrictions before tank-mixing or stacking applications in the same window.')
  }

  return limitations
}

function generateFollowUpSchedule(
  applicationType: string,
  currentSeason: string
): string[] {
  const schedule = []

  if (applicationType === 'pre-emergent' || applicationType === 'both') {
    schedule.push('Monitor for weed breakthrough 4-6 weeks after application')
    schedule.push('Apply second pre-emergent application in late summer if needed')
  }

  if (applicationType === 'post-emergent' || applicationType === 'both') {
    schedule.push('Reapply post-emergent in 2-3 weeks if weeds persist')
    schedule.push('Spot treat individual weeds as they appear')
  }

  if (currentSeason === 'spring') {
    schedule.push('Plan fall pre-emergent application for next season')
  }

  return schedule
}

function generateRegionalNotes(
  region: string,
  climateData: any,
  targetWeeds: string[]
): string[] {
  const notes = []

  if (climateData.commonChallenges.includes('crabgrass')) {
    notes.push('Crabgrass is a major problem in this region - pre-emergent timing is critical')
  }

  if (region.includes('Southeast') || region.includes('South')) {
    notes.push('Earlier application timing due to warmer climate')
    notes.push('Higher humidity may affect herbicide performance')
  }

  if (region.includes('North') || region.includes('Great Lakes')) {
    notes.push('Later application timing due to cooler climate')
    notes.push('Shorter growing season requires precise timing')
  }

  return notes
}

function generateWeatherConsiderations(
  currentSeason: string,
  region: string
): string[] {
  const considerations = []

  switch (currentSeason) {
    case 'spring':
      considerations.push('Monitor soil temperature for optimal timing')
      considerations.push('Avoid application during heavy rainfall periods')
      break
    case 'summer':
      considerations.push('Apply early morning or evening to avoid heat stress')
      considerations.push('Ensure adequate soil moisture before application')
      break
    case 'fall':
      considerations.push('Excellent conditions for herbicide application')
      considerations.push('Cooler temperatures reduce stress on grass')
      break
    case 'winter':
      considerations.push('Wait for warmer weather and active weed growth')
      considerations.push('Focus on planning for spring applications')
      break
  }

  return considerations
}

function determineCurrentStatus(
  preEmergentTiming: HerbicideTimingResult['preEmergentTiming'] | undefined,
  postEmergentTiming: HerbicideTimingResult['postEmergentTiming'] | undefined,
  recommendedApproach: HerbicideTimingResult['recommendedApproach']
): string {
  if (recommendedApproach === 'pre-emergent' && preEmergentTiming) {
    return preEmergentTiming.status
  }
  if (recommendedApproach === 'post-emergent' && postEmergentTiming) {
    return postEmergentTiming.status
  }
  // Fall back to whichever timing exists if the recommended approach
  // somehow lacks data (defensive — shouldn't happen given the decision logic).
  if (preEmergentTiming) return preEmergentTiming.status
  if (postEmergentTiming) return postEmergentTiming.status
  return 'Enter your details to get personalized herbicide timing.'
}

function determineNextAction(
  preEmergentTiming: HerbicideTimingResult['preEmergentTiming'] | undefined,
  postEmergentTiming: HerbicideTimingResult['postEmergentTiming'] | undefined,
  recommendedApproach: HerbicideTimingResult['recommendedApproach']
): string {
  if (recommendedApproach === 'pre-emergent' && preEmergentTiming) {
    return preEmergentNextAction(preEmergentTiming, !!postEmergentTiming)
  }
  if (recommendedApproach === 'post-emergent' && postEmergentTiming) {
    return postEmergentNextAction(postEmergentTiming)
  }
  return 'Review your herbicide timing results and product recommendations below.'
}

function preEmergentNextAction(
  preEmergentTiming: NonNullable<HerbicideTimingResult['preEmergentTiming']>,
  hasPostEmergent: boolean
): string {
  const days = preEmergentTiming.daysRemaining
  const window = preEmergentTiming.optimalWindow

  const soil = preEmergentTiming.soilBasis
  if (soil?.stage === 'open') {
    return `Apply pre-emergent now, then water it in. The window closes when soil drops below ${soil.closesBelowF}°F.`
  }
  if (soil?.stage === 'waiting') {
    return `Hold the pre-emergent until soil cools below ${soil.opensBelowF}°F. It is ${soil.soilTempF}°F now.`
  }

  switch (preEmergentTiming.urgencyLevel) {
    case 'critical':
      if (days <= 3) {
        return `Apply pre-emergent today or tomorrow. The ${window} window closes in ${days} day${days === 1 ? '' : 's'}.`
      }
      return `Apply pre-emergent within the next ${days} days. The ${window} window is closing soon.`

    case 'optimal':
      if (days > 60) {
        const months = Math.round(days / 30)
        return `Your pre-emergent window (${window}) opens in about ${months} months. Set a calendar reminder.`
      }
      if (days > 30) {
        return `Your pre-emergent window is ${window}. Start planning your application in the coming weeks.`
      }
      return `Apply pre-emergent soon. You have ${days} days left in the ${window} window.`

    case 'late':
      return 'The optimal pre-emergent window has passed. You can still apply, but effectiveness may be reduced.'

    case 'too-late':
      if (preEmergentTiming.seasonOver) {
        return `Skip spraying this fall. Put down a pre-emergent next spring${preEmergentTiming.nextWindow ? `: ${preEmergentTiming.nextWindow}` : ''}.`
      }
      if (hasPostEmergent) {
        return `Too late for pre-emergent this season.${preEmergentTiming.nextWindow ? ` The next window is ${preEmergentTiming.nextWindow}.` : ''} Switch focus to post-emergent treatment of visible weeds.`
      }
      return `Too late for pre-emergent this season. ${preEmergentTiming.nextWindow ? `The next window is ${preEmergentTiming.nextWindow}.` : 'Plan for the next application window.'}`
  }
}

function postEmergentNextAction(
  postEmergentTiming: NonNullable<HerbicideTimingResult['postEmergentTiming']>
): string {
  if (postEmergentTiming.hold) return postEmergentTiming.hold.text
  if (postEmergentTiming.currentConditions.includes('Excellent')) {
    return `Conditions are excellent for post-emergent application. Apply during ${postEmergentTiming.optimalWindow} for best results.`
  }
  if (postEmergentTiming.currentConditions.includes('Good')) {
    return `Good conditions for post-emergent application. Target weeds during active growth.`
  }
  if (postEmergentTiming.currentConditions.includes('Hot') || postEmergentTiming.currentConditions.includes('heat')) {
    return 'Apply post-emergent early morning or evening to avoid heat stress. Keep temperatures below 85°F.'
  }
  if (postEmergentTiming.currentConditions.includes('dormant') || postEmergentTiming.currentConditions.includes('Poor')) {
    return 'Weeds are currently dormant. Wait for active growth in spring before applying post-emergent.'
  }
  return `Apply post-emergent during ${postEmergentTiming.optimalWindow} when weeds are actively growing.`
}

/**
 * Season-to-date mapping for parsing window strings.
 * Maps season phrases to approximate month-day values.
 */
const SEASON_DATE_MAP: Record<string, string> = {
  'early spring': 'March 15',
  'mid spring': 'April 15',
  'late spring': 'May 15',
  'early summer': 'June 15',
  'mid summer': 'July 15',
  'late summer': 'August 15',
  'early fall': 'September 15',
  'mid fall': 'October 15',
  'late fall': 'November 15',
  'early winter': 'December 15',
  'mid winter': 'January 15',
  'late winter': 'February 15',
  // Single-word seasons (used as fallback)
  'spring': 'April 30',
  'summer': 'July 31',
  'fall': 'October 31',
  'winter': 'January 31',
}

const INVALID_WINDOW_PATTERN = /ineffective|not\s+seed|primarily|generally/i
const RANGE_SEPARATOR_PATTERN = /\s*[-–]\s*|\s+to\s+/i
const ALTERNATIVE_SEPARATOR_PATTERN = /\s+or\s+/i
const MONTH_DAY_PATTERN = /\b(january|february|march|april|may|june|july|august|september|october|november|december)\s+\d{1,2}\b/i
const SEASON_NOUN_PATTERN = /\b(spring|summer|fall|winter)\b/i

/**
 * Try to resolve a string fragment to a calendar date.
 * Returns null if the string doesn't look like a date or season phrase.
 */
function resolveToDate(input: string): Date | null {
  const trimmed = input.trim().toLowerCase()

  // Reject strings that are clearly not timing windows
  if (!trimmed || trimmed.length > 40) return null
  if (INVALID_WINDOW_PATTERN.test(trimmed)) return null

  const qualifierMatch = trimmed.match(/^(early|mid|late)$/i)
  if (qualifierMatch) {
    return null
  }

  const literalDateMatch = trimmed.match(MONTH_DAY_PATTERN)
  if (literalDateMatch) {
    return monthDayDate(literalDateMatch[0], new Date().getFullYear())
  }

  // Check season map (try longest matches first)
  const seasonKeys = Object.keys(SEASON_DATE_MAP).sort((a, b) => b.length - a.length)
  for (const key of seasonKeys) {
    if (trimmed === key || trimmed.includes(key)) {
      return monthDayDate(SEASON_DATE_MAP[key], new Date().getFullYear())
    }
  }

  return null
}

const MONTH_INDEX: Record<string, number> = {
  january: 0, february: 1, march: 2, april: 3, may: 4, june: 5,
  july: 6, august: 7, september: 8, october: 9, november: 10, december: 11,
}

/**
 * "march 15" as a local date in `year`. Built from numbers, not by parsing a
 * date string: Hermes (the mobile app's engine) does not parse "march 15, 2026",
 * which left every calendar window unparsed on the phone.
 */
function monthDayDate(text: string, year: number): Date | null {
  const match = text.trim().toLowerCase().match(/^([a-z]+)\s+(\d{1,2})$/)
  if (!match) return null
  const month = MONTH_INDEX[match[1]]
  const day = Number(match[2])
  if (month === undefined || !Number.isFinite(day) || day < 1 || day > 31) return null
  return new Date(year, month, day)
}

function resolvePartialSeason(input: string, sibling: string): Date | null {
  const qualifierMatch = input.trim().toLowerCase().match(/^(early|mid|late)$/i)
  const seasonMatch = sibling.trim().toLowerCase().match(SEASON_NOUN_PATTERN)

  if (!qualifierMatch || !seasonMatch) {
    return null
  }

  return resolveToDate(`${qualifierMatch[1]} ${seasonMatch[1]}`)
}

function parseWindowOption(option: string): { start: Date; end: Date } | null {
  // Strip clarifying parentheticals like "(August-September)" so the inner dash
  // doesn't confuse the range splitter. The original windowStr is preserved on the
  // result for display; only the parsed dates are affected.
  const trimmed = option.replace(/\s*\([^)]*\)\s*/g, ' ').replace(/\s+/g, ' ').trim()
  if (!trimmed || INVALID_WINDOW_PATTERN.test(trimmed)) {
    return null
  }

  const rangeParts = trimmed
    .split(RANGE_SEPARATOR_PATTERN)
    .map((part) => part.trim())
    .filter(Boolean)

  if (rangeParts.length > 1) {
    const firstPart = rangeParts[0]
    const lastPart = rangeParts[rangeParts.length - 1]
    const start =
      resolvePartialSeason(firstPart, lastPart) ||
      resolveToDate(firstPart) ||
      resolveToDate(trimmed)
    const end =
      resolvePartialSeason(lastPart, firstPart) ||
      resolveToDate(lastPart) ||
      resolveToDate(trimmed)

    if (!start && !end) {
      return null
    }

    const resolvedStart = start || end!
    const resolvedEnd = new Date(end || start!)
    if (resolvedEnd < resolvedStart) {
      resolvedEnd.setFullYear(resolvedEnd.getFullYear() + 1)
    }

    return {
      start: resolvedStart,
      end: resolvedEnd,
    }
  }

  const singleDate = resolveToDate(trimmed)
  if (!singleDate) {
    return null
  }

  return { start: singleDate, end: singleDate }
}

function adjustWindowOption(option: { start: Date; end: Date }): { start: Date; end: Date } {
  const adjustedStart = new Date(option.start)
  const adjustedEnd = new Date(option.end)
  const now = new Date()
  // Six months is the right threshold: a window that ended 3-5 months ago is "you missed
  // it this year, switch tactics" (current season is still relevant). Beyond 6 months,
  // the user is reasonably planning for next year.
  const sixMonthsAgo = new Date(now)
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6)

  if (adjustedEnd < sixMonthsAgo) {
    adjustedStart.setFullYear(adjustedStart.getFullYear() + 1)
    adjustedEnd.setFullYear(adjustedEnd.getFullYear() + 1)
  }

  return { start: adjustedStart, end: adjustedEnd }
}

function reduceWindowToChosenHalf(windowStr: string, chosenEnd: Date): string {
  const halves = windowStr
    .split(ALTERNATIVE_SEPARATOR_PATTERN)
    .map((s) => s.trim())
    .filter(Boolean)
  if (halves.length < 2) return windowStr
  const oneDayMs = 24 * 60 * 60 * 1000
  for (const half of halves) {
    // Use parseTimingWindow (not parseWindowOption) so adjustWindowOption's year-shift
    // logic is applied — otherwise winter calls miss the year-rolled "early spring" half.
    const parsed = parseTimingWindow(half)
    if (parsed && Math.abs(parsed.end.getTime() - chosenEnd.getTime()) < oneDayMs) {
      return half
    }
  }
  return windowStr
}

function parseTimingWindow(windowStr: string): { start: Date; end: Date } | null {
  if (INVALID_WINDOW_PATTERN.test(windowStr)) {
    return null
  }

  const options = windowStr
    .split(ALTERNATIVE_SEPARATOR_PATTERN)
    .map((option) => parseWindowOption(option))
    .filter((option): option is { start: Date; end: Date } => Boolean(option))
    .map(adjustWindowOption)

  if (options.length === 0) {
    return null
  }

  const now = new Date()
  const scoredOptions = options.map((option) => ({
    option,
    days: Math.round((option.end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)),
  }))

  const futureOptions = scoredOptions.filter(({ days }) => days > 0)
  const bestOption = futureOptions.length > 0
    ? futureOptions.reduce((best, current) => current.days < best.days ? current : best)
    : scoredOptions.reduce((best, current) => current.days > best.days ? current : best)

  return bestOption.option
}

/**
 * Parse the end date from an optimal window string like "March 15 - April 15",
 * "Late summer to early fall", "Early to mid fall", or "Fall or early spring".
 * Returns a Date for the current year (or next year if the date has already
 * passed by more than 2 months). Returns null for non-timing strings.
 */
export function parseWindowEndDate(windowStr: string): Date | null {
  return parseTimingWindow(windowStr)?.end || null
}

/**
 * Parse the start date from an optimal window string.
 * Used by the calendar helper to set the reminder date.
 */
export function parseWindowStartDate(windowStr: string): Date | null {
  return parseTimingWindow(windowStr)?.start || null
}
